import React, { useEffect, useState, useContext } from "react";
import { Link } from "react-router-dom";
import { GlobalContext } from "../../context/globalContext";
import {
  downloadOrderInvoice,
  downloadOrderReceipt,
  receiptFilename,
  getMyOrders,
  Order,
  triggerBlobDownload,
} from "../../api/orders";
import {
  getMyRegistrations,
  MyRegistration,
} from "../../api/registration";
import ErrorAlert from "../../components/ErrorAlert/ErrorAlert";
import { getUserFacingErrorMessage } from "../../utils/userFacingError";
import {
  createRefundRequest,
  getMyRefundRequests,
  RefundRequest,
} from "../../api/refund";
import {
  CANCELLATION_ADMIN_FEE_USD,
  CANCELLATION_POLICY_SUMMARY,
  formatNetRefundUsd,
  isCancellationAllowed,
} from "../../utils/cancellationPolicy";
import getSymbolFromCurrency from "currency-symbol-map";
import LoadingPanel from "../../components/LoadingPanel/LoadingPanel";

function isRefundInProgress(refund: RefundRequest): boolean {
  return refund.status === "pending" && Boolean(refund.stripeRefundId);
}

function isRefundFailed(refund: RefundRequest): boolean {
  return (
    refund.status === "failed" ||
    (refund.status === "pending" && !refund.stripeRefundId)
  );
}

const emailAddress = { tech: "contact@eventshouse.net" };

/** Shared sizing for Past order action buttons (links + buttons). */
const ORDER_ACTION_BTN =
  "inline-flex items-center justify-center h-8 px-3 text-xs font-medium rounded-sm box-border shrink-0";

export default function UserPageOrders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [registrations, setRegistrations] = useState<MyRegistration[]>([]);
  const [refundByOrderId, setRefundByOrderId] = useState<
    Record<number, RefundRequest>
  >({});
  const [processedRefunds, setProcessedRefunds] = useState<RefundRequest[]>([]);
  const { currentUser } = useContext(GlobalContext);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (currentUser) {
        setIsLoading(true);
        setError(null);
        try {
          const [orderData, registrationData, refundData] = await Promise.all([
            getMyOrders(),
            getMyRegistrations(),
            getMyRefundRequests(),
          ]);
          setOrders(
            orderData.filter(
              (order) => order.status === "paid" || order.status === "refunded",
            ),
          );
          setRegistrations(
            registrationData.filter((r) => r.status === "confirmed"),
          );
          const refundMap: Record<number, RefundRequest> = {};
          const completed: RefundRequest[] = [];
          for (const r of refundData) {
            refundMap[r.order.id] = r;
            if (r.status === "processed") completed.push(r);
          }
          setRefundByOrderId(refundMap);
          setProcessedRefunds(completed);
        } catch (err) {
          console.error("Error fetching account data:", err);
          setError("Failed to fetch your orders and registrations.");
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, [currentUser]);

  return (
    <div>
      <h2 className="text-lg font-bold text-slate-900 mb-1">
        Orders &amp; registrations
      </h2>
      <p className="text-sm text-slate-600 mb-6">
        View confirmed event registrations, download receipts, and
        cancel eligible orders for a refund (subject to the cancellation policy).
      </p>

      {isLoading ? (
        <LoadingPanel
          title="Retrieving your account"
          subtitle="Loading your orders and event registrations."
          className="py-12"
        />
      ) : error ? (
        <ErrorAlert>{error}</ErrorAlert>
      ) : orders.length > 0 || registrations.length > 0 ? (
        <div className="space-y-10">
          {registrations.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-3">
                My registrations
              </h3>
              <div className="space-y-3">
                {registrations.map((registration) => (
                  <RegistrationCard
                    key={registration.registrationId}
                    registration={registration}
                  />
                ))}
              </div>
            </section>
          )}
          {processedRefunds.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-3">
                Refund history
              </h3>
              <div className="space-y-3">
                {processedRefunds.map((refund) => (
                  <RefundHistoryCard key={refund.id} refund={refund} />
                ))}
              </div>
            </section>
          )}
          {orders.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-3">
                Past orders
              </h3>
              <div className="space-y-3">
                {orders.map((order) => (
                  <PastOrder
                    key={order.id}
                    order={order}
                    refundRequest={refundByOrderId[order.id]}
                    onRefundSubmitted={async () => {
                      const [orderData, refundData] = await Promise.all([
                        getMyOrders(),
                        getMyRefundRequests(),
                      ]);
                      setOrders(
                        orderData.filter(
                          (order) =>
                            order.status === "paid" || order.status === "refunded",
                        ),
                      );
                      setRegistrations(
                        (await getMyRegistrations()).filter(
                          (r) => r.status === "confirmed",
                        ),
                      );
                      const refundMap: Record<number, RefundRequest> = {};
                      const completed: RefundRequest[] = [];
                      for (const r of refundData) {
                        refundMap[r.order.id] = r;
                        if (r.status === "processed") completed.push(r);
                      }
                      setRefundByOrderId(refundMap);
                      setProcessedRefunds(completed);
                    }}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      ) : (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-6 text-center">
          <p className="text-sm font-medium text-slate-900 mb-2">
            No orders or registrations yet
          </p>
          <p className="text-sm text-slate-600 leading-relaxed max-w-md mx-auto mb-5">
            When you complete an event registration or purchase, it will appear
            here. You can download receipts and invoices from this
            page.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/" className="eh-btn-primary px-5 py-2 text-sm font-medium">
              Browse events
            </Link>
            <Link
              to="/user/dashboard"
              className="eh-btn-secondary px-5 py-2 text-sm font-medium"
            >
              View saved registrations
            </Link>
          </div>
          <p className="text-xs text-slate-500 mt-5 leading-relaxed">
            Completed a purchase but nothing shows? Try refreshing this page, or
            email{" "}
            <a
              href={`mailto:${emailAddress.tech}`}
              className="font-medium text-slate-700 underline hover:text-slate-900"
            >
              {emailAddress.tech}
            </a>
            .
          </p>
        </div>
      )}
    </div>
  );
}

function RegistrationCard({ registration }: { registration: MyRegistration }) {
  const [isDownloadingReceipt, setIsDownloadingReceipt] = useState(false);
  const currency = registration.currency.toUpperCase();
  const order = registration.order;

  const handleDownloadReceipt = async () => {
    if (!order) return;
    setIsDownloadingReceipt(true);
    try {
      const blob = await downloadOrderReceipt(order.id);
      triggerBlobDownload(
        blob,
        receiptFilename({ ...order, registrationId: registration.registrationId }),
      );
    } catch (err) {
      console.error("Error downloading receipt:", err);
    } finally {
      setIsDownloadingReceipt(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-sm bg-white p-4 sm:p-5">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="text-sm space-y-1">
          <p className="font-semibold text-slate-900">{registration.event.title}</p>
          <p className="text-slate-600">
            <span className="text-slate-500">Registration ID</span>{" "}
            <span className="font-medium text-slate-800">
              {registration.registrationId}
            </span>
          </p>
          <p className="text-slate-700">
            {registration.firstName} {registration.lastName} —{" "}
            {registration.itemOrdered}
          </p>
          {registration.banquetQuantity > 0 && (
            <p className="text-slate-600">
              Banquet tickets: {registration.banquetQuantity}
            </p>
          )}
          {registration.registrationDate && (
            <p className="text-slate-500 text-xs pt-1">
              Registered {registration.registrationDate.slice(0, 10)}
            </p>
          )}
        </div>
        <div className="text-sm sm:text-right shrink-0">
          <p className="font-semibold text-slate-900">
            {getSymbolFromCurrency(currency)}
            {(registration.amountPaidPence / 100).toFixed(2)}
          </p>
          {order?.status === "paid" && (
            <button
              type="button"
              className="eh-btn-primary px-4 py-1.5 mt-3 text-xs font-medium"
              onClick={handleDownloadReceipt}
              disabled={isDownloadingReceipt}
            >
              {isDownloadingReceipt ? "Downloading…" : "Download receipt (PDF)"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function RefundHistoryCard({ refund }: { refund: RefundRequest }) {
  const currency = refund.order.currency.toUpperCase();
  const symbol = getSymbolFromCurrency(currency);
  const processedDate = (refund.reviewedAt ?? refund.createdAt).slice(0, 10);

  return (
    <div className="border border-slate-200 rounded-sm bg-white p-4 sm:p-5">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="text-sm space-y-1">
          <p className="font-semibold text-slate-900">{refund.event.title}</p>
          <p className="text-slate-600">
            Order {refund.order.orderNumber ?? refund.order.id}
          </p>
          <p className="text-slate-500 text-xs pt-1">
            Refunded on {processedDate}
          </p>
        </div>
        <div className="text-sm sm:text-right shrink-0">
          <p className="font-semibold text-slate-900">
            {symbol}
            {(refund.amountPence / 100).toFixed(2)} refunded
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {refund.amountPence === refund.order.totalAmountPence
              ? "Full refund (test pricing)"
              : `$${CANCELLATION_ADMIN_FEE_USD} admin fee deducted from original ${symbol}${(refund.order.totalAmountPence / 100).toFixed(2)}`}
          </p>
        </div>
      </div>
    </div>
  );
}

function PastOrder(props: {
  order: Order;
  refundRequest?: RefundRequest;
  onRefundSubmitted: () => Promise<void>;
}) {
  const [isDownloadingReceipt, setIsDownloadingReceipt] = useState(false);
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  const [isSubmittingRefund, setIsSubmittingRefund] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);
  const currency = props.order.currency.toUpperCase();
  const cancellationAllowed = isCancellationAllowed();
  const canRequestRefund =
    props.order.status === "paid" &&
    (!props.refundRequest ||
      isRefundFailed(props.refundRequest)) &&
    cancellationAllowed;

  const handleRefundSubmit = async () => {
    if (refundReason.trim().length < 5) {
      setRefundError("Please provide a reason (at least 5 characters).");
      return;
    }
    setIsSubmittingRefund(true);
    setRefundError(null);
    try {
      await createRefundRequest(props.order.id, refundReason.trim());
      setShowRefundForm(false);
      setRefundReason("");
      await props.onRefundSubmitted();
    } catch (err) {
      setRefundError(
        getUserFacingErrorMessage(err, "Refund request failed."),
      );
      await props.onRefundSubmitted();
    } finally {
      setIsSubmittingRefund(false);
    }
  };

  const handleDownloadReceipt = async () => {
    setIsDownloadingReceipt(true);
    try {
      const blob = await downloadOrderReceipt(props.order.id);
      triggerBlobDownload(blob, receiptFilename(props.order));
    } catch (error) {
      console.error("Error downloading receipt:", error);
    } finally {
      setIsDownloadingReceipt(false);
    }
  };

  const handleDownloadInvoice = async () => {
    setIsDownloadingInvoice(true);
    try {
      const blob = await downloadOrderInvoice(props.order.id);
      triggerBlobDownload(
        blob,
        `${props.order.orderNumber ?? `order-${props.order.id}`}-invoice.pdf`,
      );
    } catch (error) {
      console.error("Error downloading invoice:", error);
    } finally {
      setIsDownloadingInvoice(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-sm bg-white p-4 sm:p-5">
      <div className="flex flex-col sm:flex-row justify-between gap-4 pb-4 border-b border-slate-100">
        <div className="flex flex-col text-sm items-start min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {props.order.payment?.receiptPdfAvailable && (
              <button
                className={`${ORDER_ACTION_BTN} eh-btn-primary !py-0`}
                type="button"
                onClick={handleDownloadReceipt}
                disabled={isDownloadingReceipt}
              >
                {isDownloadingReceipt ? "Downloading…" : "Download receipt"}
              </button>
            )}
            {props.order.payment?.invoiceAvailable && (
              <button
                className={`${ORDER_ACTION_BTN} eh-btn-secondary !py-0`}
                type="button"
                onClick={handleDownloadInvoice}
                disabled={isDownloadingInvoice}
              >
                {isDownloadingInvoice ? "Downloading…" : "Stripe invoice"}
              </button>
            )}
            {props.order.payment?.receiptUrl && (
              <a
                className={`${ORDER_ACTION_BTN} eh-btn-secondary !py-0`}
                href={props.order.payment.receiptUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Stripe receipt
              </a>
            )}
            {canRequestRefund && (
              <button
                type="button"
                className={`${ORDER_ACTION_BTN} border border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100 transition-colors`}
                onClick={() => setShowRefundForm(true)}
              >
                Cancel &amp; refund
              </button>
            )}
          </div>
          {props.refundRequest && !isRefundFailed(props.refundRequest) && (
            <p className="text-xs text-slate-600 mb-2">
              {props.refundRequest.status === "processed" ? (
                <>
                  Refunded{" "}
                  <span className="font-medium">
                    {getSymbolFromCurrency(currency)}
                    {(props.refundRequest.amountPence / 100).toFixed(2)}
                  </span>{" "}
                  on{" "}
                  {(
                    props.refundRequest.reviewedAt ??
                    props.refundRequest.createdAt
                  ).slice(0, 10)}
                </>
              ) : isRefundInProgress(props.refundRequest) ? (
                <span className="text-amber-800">
                  Refund processing — Alipay refunds can take a few minutes to
                  appear.
                </span>
              ) : (
                <>
                  Refund:{" "}
                  <span className="font-medium capitalize">
                    {props.refundRequest.status}
                  </span>
                </>
              )}
            </p>
          )}
          {props.refundRequest && isRefundFailed(props.refundRequest) && (
            <p className="text-xs text-red-700 mb-2 font-medium">
              Refund failed
              {props.refundRequest.reviewNote
                ? ` — ${props.refundRequest.reviewNote}`
                : " — please contact support or try again"}
            </p>
          )}
          {props.order.status === "refunded" && !props.refundRequest && (
            <p className="text-xs text-slate-600 mb-2 font-medium">Refunded</p>
          )}
          {showRefundForm && (
            <div className="mb-3 p-4 border border-amber-200 bg-amber-50 rounded-sm w-full max-w-md">
              <p className="text-xs font-semibold text-amber-900 mb-2">
                Cancel registration
              </p>
              <p className="text-xs text-amber-900 mb-3 leading-relaxed">
                {CANCELLATION_POLICY_SUMMARY}{" "}
                {props.order.waivesCancellationAdminFee ? (
                  <>
                    You will receive a full refund of{" "}
                    {getSymbolFromCurrency(currency)}
                    {(props.order.totalAmountPence / 100).toFixed(2)}. Refunds
                    are processed immediately.
                  </>
                ) : (
                  <>
                    A ${CANCELLATION_ADMIN_FEE_USD} administrative fee will be
                    deducted. You will receive {getSymbolFromCurrency(currency)}
                    {formatNetRefundUsd(
                      props.order.totalAmountPence / 100,
                    ).toFixed(2)}{" "}
                    back. Refunds are processed immediately.
                  </>
                )}
              </p>
              <textarea
                className="w-full border border-slate-300 rounded-sm bg-white p-2 text-xs mb-2 focus:outline-none focus:ring-2 focus:ring-slate-400"
                rows={3}
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder="Reason for refund request…"
              />
              {refundError && (
                <ErrorAlert inline className="mb-2">
                  {refundError}
                </ErrorAlert>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  className="eh-btn-primary text-xs"
                  disabled={isSubmittingRefund}
                  onClick={handleRefundSubmit}
                >
                  {isSubmittingRefund ? "Processing…" : "Confirm cancellation"}
                </button>
                <button
                  type="button"
                  className="eh-btn-secondary text-xs"
                  onClick={() => setShowRefundForm(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          <div className="space-y-1 text-slate-700">
            <p>
              <span className="text-slate-500">Date</span>{" "}
              <span className="font-medium text-slate-900">
                {props.order.createdAt.slice(0, 10)}
              </span>
            </p>
            <p>
              <span className="text-slate-500">Order no.</span>{" "}
              <span className="font-medium text-slate-900">
                {props.order.orderNumber ?? props.order.id}
              </span>
            </p>
            {props.order.registrationId && (
              <p>
                <span className="text-slate-500">Registration</span>{" "}
                <span className="font-medium text-slate-900">
                  {props.order.registrationId}
                </span>
              </p>
            )}
          </div>
        </div>
        <div className="text-sm sm:text-right shrink-0">
          <p className="font-semibold text-slate-900">
            {getSymbolFromCurrency(currency)}
            {(props.order.totalAmountPence / 100).toFixed(2)}
          </p>
          <p className="text-xs text-slate-500 mt-1">Paid via Stripe</p>
        </div>
      </div>
      <div className="pt-4 text-sm space-y-1">
        {props.order.orderItems.map((item) => (
          <div
            key={item.id}
            className="flex justify-between gap-4 py-1.5 text-slate-700"
          >
            <span>
              {item.event.title} — {item.ticketType.name} ×{item.quantity}
            </span>
            <span className="font-medium text-slate-900 shrink-0">
              {getSymbolFromCurrency(currency)}
              {(item.subtotalPence / 100).toFixed(2)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
